/**
 * `describeStandard` (standards selects spec §2, §4): a structured standard as words. Pure
 * and shared, so the server's prompt lines and the panel and plugin selects agree on what a
 * preset means.
 *
 * - `label`: the short text a person reads ("€1.2M", "Mar 2025", "FY25, year ends June").
 *   The old `/v1/standards` grace window (do-not-touch D-30) returns it to cached bundles.
 * - `prompt`: the line the model reads after "Currency format: ".
 *
 * Amounts are written in English number style (comma thousands, dot decimal): every
 * language variant on offer is English.
 */
import {
  CALENDAR_YEAR_FORMATS,
  CURRENCIES,
  CURRENCY_STYLES,
  FISCAL_STYLES,
  LANGUAGE_VARIANTS,
  MONTH_NAMES,
  type CalendarYearFormat,
  type CurrencyCode,
  type CurrencyFormat,
  type CurrencyInfo,
  type CurrencyStyle,
  type FiscalYearFormat,
  type LanguageVariant,
} from "./formats.js";

/** The standards fields that are closed selects. */
type StructuredStandardsField = "languageVariant" | "currencyFormat" | "calendarYearFormat" | "fiscalYearFormat";

type StructuredValue = {
  languageVariant: LanguageVariant;
  currencyFormat: CurrencyFormat;
  calendarYearFormat: CalendarYearFormat;
  fiscalYearFormat: FiscalYearFormat;
};

type StandardDescription = { label: string; prompt: string };

function currencyInfo(code: CurrencyCode): CurrencyInfo {
  for (let i = 0; i < CURRENCIES.length; i++) if (CURRENCIES[i].code === code) return CURRENCIES[i];
  return CURRENCIES[0];
}

function find<T extends { id: string }>(list: readonly T[], id: string): T {
  for (let i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
  return list[0];
}

/** A symbol made of letters ("CHF", "SEK") is set off by a space; "$" and "€" are not. */
function withSymbol(symbol: string, amount: string): string {
  return /[A-Za-z]$/.test(symbol) ? `${symbol} ${amount}` : `${symbol}${amount}`;
}

/** 1.2 million in `currency`, written in `style` ("€1.2M", "EUR 1.2 million"). */
export function currencyExample(currency: CurrencyCode, style: CurrencyStyle): string {
  const info = currencyInfo(currency);
  switch (style) {
    case "symbol-short":
      return withSymbol(info.symbol, "1.2M");
    case "symbol-full":
      return withSymbol(info.symbol, "1,200,000");
    case "code-short":
      return `${info.code} 1.2M`;
    case "symbol-word":
      return withSymbol(info.symbol, "1.2 million");
    case "code-word":
      return `${info.code} 1.2 million`;
  }
}

function describeCurrency(value: CurrencyFormat): StandardDescription {
  const info = currencyInfo(value.currency);
  const example = currencyExample(value.currency, value.style);
  const rule = find(CURRENCY_STYLES, value.style).rule;
  return { label: example, prompt: `${info.noun} (${info.code}), written like ${example} (${rule})` };
}

/** "Short month and year" → "short month and year"; an acronym ("FY", "ISO") stays. */
function inline(label: string): string {
  return /^[A-Z][a-z]/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label;
}

function describeFiscal(value: FiscalYearFormat): StandardDescription {
  const preset = find(FISCAL_STYLES, value.style);
  const month = value.endMonth ? MONTH_NAMES[value.endMonth - 1] : null;
  return {
    label: month ? `${preset.example}, year ends ${month}` : preset.example,
    prompt: `written like ${preset.example} (${inline(preset.label)})${month ? `, with the fiscal year ending in ${month}` : ""}`,
  };
}

export function describeStandard<F extends StructuredStandardsField>(field: F, value: StructuredValue[F]): StandardDescription {
  switch (field) {
    case "currencyFormat":
      return describeCurrency(value as CurrencyFormat);
    case "fiscalYearFormat":
      return describeFiscal(value as FiscalYearFormat);
    case "calendarYearFormat": {
      const preset = find(CALENDAR_YEAR_FORMATS, value as CalendarYearFormat);
      return { label: preset.example, prompt: `written like ${preset.example} (${inline(preset.label)})` };
    }
    default: {
      const variant = find(LANGUAGE_VARIANTS, value as LanguageVariant);
      return { label: variant.label, prompt: `${variant.label} (${variant.id})` };
    }
  }
}
